import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import WorkIcon from '@mui/icons-material/Work';
import SectionCard from './SectionCard';
import ConfirmDialog from '../common/ConfirmDialog';
import { INDUSTRIES, MONTHS, YEARS, type WorkEntry } from './profileConstants';
import userService, {
  type CreateUserWorkHistoryDto,
  type UpdateUserWorkHistoryDto,
  type UserWorkHistoryDto,
} from '../../services/userService';

interface WorkHistoryCardProps {
  notify: (message: string) => void;
}

type WorkForm = Omit<WorkEntry, 'backendId'>;

const EMPTY_FORM: WorkForm = {
  companyName: '',
  jobTitle: '',
  industry: '',
  startMonth: '',
  startYear: '',
  isCurrent: false,
  endMonth: '',
  endYear: '',
  description: '',
};

function toMonthYear(iso: string | null | undefined): { month: string; year: string } {
  if (!iso) return { month: '', year: '' };
  const d = new Date(iso);
  return { month: MONTHS[d.getUTCMonth()] ?? '', year: String(d.getUTCFullYear()) };
}

function toIsoDate(month: string, year: string): string {
  const monthIndex = MONTHS.indexOf(month) + 1;
  return `${year}-${String(monthIndex).padStart(2, '0')}-01T00:00:00Z`;
}

/** Numeric sort key for a start date; entries without a date sort last. */
function startKey(entry: WorkEntry): number {
  if (!entry.startYear) return -1;
  return Number(entry.startYear) * 12 + Math.max(0, MONTHS.indexOf(entry.startMonth));
}

/** Current positions first, then most recent start date. */
function sortEntries(entries: WorkEntry[]): WorkEntry[] {
  return [...entries].sort((a, b) => {
    if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1;
    return startKey(b) - startKey(a);
  });
}

/** Work history list with add/edit dialog and delete confirmation. */
export default function WorkHistoryCard({ notify }: WorkHistoryCardProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [entries, setEntries] = useState<WorkEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<WorkForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const [deleteTarget, setDeleteTarget] = useState<WorkEntry | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    userService.getWorkHistoryItems()
      .then((dtos: UserWorkHistoryDto[]) => {
        setEntries(dtos.map(dto => {
          const start = toMonthYear(dto.startDate);
          const end = toMonthYear(dto.endDate);
          return {
            backendId: dto.id,
            companyName: dto.companyName,
            jobTitle: dto.jobTitle,
            industry: dto.industry,
            startMonth: start.month,
            startYear: start.year,
            isCurrent: dto.isCurrent,
            endMonth: end.month,
            endYear: end.year,
            description: dto.description,
          };
        }));
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const sorted = sortEntries(entries);

  const handleAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setSaveError('');
    setDialogOpen(true);
  };

  const handleEdit = (entry: WorkEntry) => {
    setEditingId(entry.backendId);
    setForm({
      companyName: entry.companyName,
      jobTitle: entry.jobTitle,
      industry: entry.industry,
      startMonth: entry.startMonth,
      startYear: entry.startYear,
      isCurrent: entry.isCurrent,
      endMonth: entry.endMonth,
      endYear: entry.endYear,
      description: entry.description,
    });
    setSaveError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.companyName.trim()) {
      setSaveError('Company name is required');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      const startDate = form.startMonth && form.startYear
        ? toIsoDate(form.startMonth, form.startYear)
        : new Date().toISOString();
      const endDate = form.isCurrent || !form.endMonth || !form.endYear
        ? null
        : toIsoDate(form.endMonth, form.endYear);
      const dto = {
        companyName: form.companyName,
        jobTitle: form.jobTitle,
        industry: form.industry,
        startDate,
        endDate,
        isCurrent: form.isCurrent,
        description: form.description,
      };

      if (editingId) {
        await userService.updateWorkHistoryItem(editingId, dto as UpdateUserWorkHistoryDto);
        setEntries(prev => prev.map(e => (e.backendId === editingId ? { ...form, backendId: editingId } : e)));
        notify('Position updated');
      } else {
        const created = await userService.createWorkHistoryItem(dto as CreateUserWorkHistoryDto);
        setEntries(prev => [...prev, { ...form, backendId: created.id }]);
        notify('Position added');
      }
      setDialogOpen(false);
    } catch {
      setSaveError('Failed to save work history. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await userService.deleteWorkHistoryItem(deleteTarget.backendId);
      setEntries(prev => prev.filter(e => e.backendId !== deleteTarget.backendId));
      setDeleteTarget(null);
      notify('Position deleted');
    } catch {
      setDeleteError('Failed to delete this position. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <SectionCard
      icon={<WorkIcon />}
      title="Work History"
      subtitle="Your work history helps personalize your CPE recommendations."
      action={
        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAdd}>
          Add Position
        </Button>
      }
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : loadError ? (
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}
        >
          Failed to load your work history.
        </Alert>
      ) : sorted.length === 0 ? (
        <Alert severity="info">No work history added yet. Click "Add Position" to get started.</Alert>
      ) : (
        <Stack spacing={2}>
          {sorted.map((entry, index) => (
            <Card key={entry.backendId} variant="outlined" sx={{ borderRadius: '8px' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {entry.jobTitle || '(No title)'}
                      </Typography>
                      {index === 0 && (
                        <Chip label="Most Recent" color="primary" size="small" variant="outlined" />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {entry.companyName}{entry.industry ? ` · ${entry.industry}` : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {entry.startMonth && entry.startYear ? `${entry.startMonth} ${entry.startYear}` : ''}
                      {(entry.startMonth || entry.startYear) && ' – '}
                      {entry.isCurrent ? 'Present' : (entry.endMonth && entry.endYear ? `${entry.endMonth} ${entry.endYear}` : '')}
                    </Typography>
                    {entry.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                        {entry.description.length > 120 ? `${entry.description.substring(0, 120)}…` : entry.description}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton size="small" aria-label="Edit position" onClick={() => handleEdit(entry)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      aria-label="Delete position"
                      onClick={() => { setDeleteError(''); setDeleteTarget(entry); }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{editingId ? 'Edit Position' : 'Add Position'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {saveError && <Alert severity="error">{saveError}</Alert>}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Company Name *"
                value={form.companyName}
                onChange={e => setForm(prev => ({ ...prev, companyName: e.target.value }))}
                fullWidth
                required
              />
              <TextField
                label="Job Title"
                value={form.jobTitle}
                onChange={e => setForm(prev => ({ ...prev, jobTitle: e.target.value }))}
                fullWidth
              />
            </Box>
            <FormControl fullWidth>
              <InputLabel>Industry</InputLabel>
              <Select
                label="Industry"
                value={form.industry}
                onChange={e => setForm(prev => ({ ...prev, industry: e.target.value }))}
              >
                {INDUSTRIES.map(ind => <MenuItem key={ind} value={ind}>{ind}</MenuItem>)}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Start Month</InputLabel>
                <Select
                  label="Start Month"
                  value={form.startMonth}
                  onChange={e => setForm(prev => ({ ...prev, startMonth: e.target.value }))}
                >
                  {MONTHS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Start Year</InputLabel>
                <Select
                  label="Start Year"
                  value={form.startYear}
                  onChange={e => setForm(prev => ({ ...prev, startYear: e.target.value }))}
                >
                  {YEARS.map(y => <MenuItem key={y} value={String(y)}>{y}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.isCurrent}
                  onChange={e => setForm(prev => ({ ...prev, isCurrent: e.target.checked, endMonth: '', endYear: '' }))}
                  color="primary"
                />
              }
              label="I currently work here"
            />
            {!form.isCurrent && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>End Month</InputLabel>
                  <Select
                    label="End Month"
                    value={form.endMonth}
                    onChange={e => setForm(prev => ({ ...prev, endMonth: e.target.value }))}
                  >
                    {MONTHS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>End Year</InputLabel>
                  <Select
                    label="End Year"
                    value={form.endYear}
                    onChange={e => setForm(prev => ({ ...prev, endYear: e.target.value }))}
                  >
                    {YEARS.map(y => <MenuItem key={y} value={String(y)}>{y}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
            )}
            <TextField
              label="Responsibilities & Achievements"
              value={form.description}
              onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              minRows={3}
              helperText="Brief summary of your role and key responsibilities"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !form.companyName.trim()}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          >
            {editingId ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Position"
        message={`Are you sure you want to delete "${deleteTarget?.jobTitle || deleteTarget?.companyName || 'this position'}"? This action cannot be undone.`}
        loading={deleting}
        error={deleteError || null}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </SectionCard>
  );
}
